import { computed } from 'vue'
import type { Task, TaskStatus } from '@/types'
import type { TasksState } from './state'

export const createGetters = (state: TasksState) => {
  const filteredTasks = computed(() => {
    let result = state.tasks

    // Filter archived by default
    if (!state.filters.showArchived) {
      result = result.filter((t) => !t.archived)
    }

    if (state.filters.search) {
      const lower = state.filters.search.toLowerCase()
      result = result.filter((t) => t.title.toLowerCase().includes(lower))
    }
    if (state.filters.status !== 'all') {
      result = result.filter((t) => t.status === state.filters.status)
    }
    if (state.filters.priority !== 'all') {
      result = result.filter((t) => t.priority === state.filters.priority)
    }
    if (state.filters.type !== 'all') {
      result = result.filter((t) => t.type === state.filters.type)
    }
    if (state.filters.assignedTo !== 'all') {
      result = result.filter((t) => t.assignedTo.includes(state.filters.assignedTo as string))
    }
    if (state.filters.projectId !== 'all') {
      result = result.filter((t) => t.projectId === state.filters.projectId)
    }

    return result
  })

  const tasksByStatus = computed(() => {
    const grouped: Record<TaskStatus, Task[]> = {
      backlog: [],
      todo: [],
      in_progress: [],
      review: [],
      done: [],
      blocked: [],
      cancelled: [],
    }

    filteredTasks.value.forEach((task) => {
      if (grouped[task.status]) {
        grouped[task.status].push(task)
      }
    })

    // Sort each column by boardOrder
    Object.keys(grouped).forEach((status) => {
      grouped[status as TaskStatus].sort((a, b) => a.boardOrder - b.boardOrder)
    })

    return grouped
  })

  const stats = computed(() => ({
    total: state.tasks.length,
    backlog: state.tasks.filter((t) => t.status === 'backlog').length,
    todo: state.tasks.filter((t) => t.status === 'todo').length,
    inProgress: state.tasks.filter((t) => t.status === 'in_progress').length,
    review: state.tasks.filter((t) => t.status === 'review').length,
    done: state.tasks.filter((t) => t.status === 'done').length,
    blocked: state.tasks.filter((t) => t.status === 'blocked').length,
  }))

  const tasksByList = computed(() => {
    const grouped: Record<string, Task[]> = {}

    filteredTasks.value.forEach((task) => {
      const listId = task.listId || '_unassigned'
      if (!grouped[listId]) grouped[listId] = []
      grouped[listId].push(task)
    })

    // Sort each list by boardOrder
    Object.keys(grouped).forEach((listId) => {
      grouped[listId].sort((a, b) => a.boardOrder - b.boardOrder)
    })

    return grouped
  })

  const overdueTasks = computed(() => {
    const now = new Date()
    return state.tasks.filter(
      (t) => t.dueDate && t.dueDate < now && t.status !== 'done' && t.status !== 'cancelled'
    )
  })

  const subtasksOf = (parentId: string) => {
    return computed(() => state.tasks.filter((t) => t.parentTaskId === parentId))
  }

  return {
    filteredTasks,
    tasksByStatus,
    tasksByList,
    stats,
    overdueTasks,
    subtasksOf,
  }
}
